import { toSentenceCase } from "@visioni/a3s-ui/utils";
import type { CameraAssetGroup } from "../client-messages/vehicles";
import { VEHICLE_TYPE_LABELS, getAttributeLabel, getTrayTypeLabel } from "../constants";
import { cameraName } from "../domain/camera-name";
import type { VehicleFilters } from "../types";
import { formatDateTime, getTimezoneAbbreviation } from "./date-utils";
import { decodeAttributes, filtersToApiQuery } from "./filter-params";

export interface ExportSummaryInput {
  /** The applied (committed) filters. */
  filters: VehicleFilters;
  /** Name of the selected site, or null for all sites. */
  siteName: string | null;
  /** IANA timezone the screen shows times in. */
  timezone: string;
  /** Cameras response, for camera nicknames. */
  cameras: readonly CameraAssetGroup[];
}

const TYPE_LABELS = new Map<string, string>(Object.entries(VEHICLE_TYPE_LABELS));

export interface ExportSummaryItem {
  label: string;
  value: string;
}

/**
 * What an export will include, as label/value pairs: the site, date range and
 * timezone, then only the filters that are applied. Text filters are read from
 * the API query, so a value the search ignores (a plate under 4 characters, say)
 * isn't listed.
 */
export function describeExportFilters({
  filters,
  siteName,
  timezone,
  cameras,
}: ExportSummaryInput): ExportSummaryItem[] {
  const query = filtersToApiQuery(filters, null);
  const { tray, sign_text: signText, trailer } = decodeAttributes(query.attributes);
  const applied: [string, string | undefined][] = [
    ["Plate", query.plateText],
    ["Vehicle type", list(filters.types.map((type) => sentenceCase(TYPE_LABELS.get(type) ?? type)))],
    ["Make", query.make],
    ["Model", query.model],
    ["Colour", query.color],
    ["Description", query.description],
    ["Load status", list(filters.loadStatuses.map(sentenceCase))],
    // Narrowed by type as the filter bar reads them: a stale URL can carry a value of the wrong shape.
    ["Tray type", typeof tray === "string" ? getTrayTypeLabel(tray) : undefined],
    ["Sign text", typeof signText === "string" ? signText : undefined],
    [getAttributeLabel("trailer"), trailer === true ? "Yes" : undefined],
    [
      "Cameras",
      list(
        Object.entries(filters.selectedCameras).flatMap(([assetId, cameraIds]) =>
          cameraIds.map((cameraId) => cameraName(assetId, cameraId, cameras)),
        ),
      ),
    ],
  ];
  return [
    { label: "Site", value: siteName ?? "All sites" },
    {
      label: "Date range",
      value: `${formatDateTime(filters.startDate, timezone)} – ${formatDateTime(filters.endDate, timezone)}`,
    },
    { label: "Timezone", value: `${getTimezoneAbbreviation(timezone)} (${timezone.replace(/_/g, " ")})` },
    ...applied.flatMap(([label, value]) => (value ? [{ label, value }] : [])),
  ];
}

/** As the filter bar labels options: "SEMI-TRAILER" and "semi-trailer" both read "Semi-trailer". */
function sentenceCase(label: string): string {
  return toSentenceCase(label, { lowercaseRest: true });
}

function list(values: string[]): string | undefined {
  return values.length > 0 ? values.join(", ") : undefined;
}
